<template>
    <div class="datepicker-container mt-5" :class="{ 'hasResults': showDatePicker }">
	    <!-- Question -->
	    <div v-if="data.meta" class="d-flex flex-column gap-3 text-center mb-5">
		    <h3 v-if="questionDescription" class="fw-semi-bold m-0" v-html="questionDescription"></h3>
		    <p v-if="questionSubText" class="text-secondary m-0" v-html="questionSubText"></p>
	    </div>

        <!-- Input -->
        <div class="d-flex align-items-center justify-content-between rounded-pill border border-secondary py-2 px-3 input-container border-active-darken"
            @click="toggleDatePicker">
            <i class="me-3 dd-icon bi bi-calendar3"></i>
            <input ref="dateInputEl" type="text" class="form-control m-0 border-0 fs-6 p-0 lh-fixed" readonly
                :value="selectedDate" placeholder="Select a date">
        </div>

        <!-- Calendar Popup -->
        <div class="d-flex flex-column gap-1 px-0 py-3 gap-1 border border-secondary picker-popup"
            v-if="showDatePicker">
            <span class="caret"></span>

            <!-- Header -->
            <div class="d-flex justify-content-between align-items-center picker-header pb-2">
                <!-- Year toggler -->
                <div class="d-flex align-items-center justify-content-center bg-hover-gray dp-nav-btn rounded-pill"
                    v-ripple @click="toggleView">
                    <button class="btn btn-transparent border-0 pe-0">
                        {{ months[currentMonth] + ' ' + currentYear }}
                    </button>
                    <i class="bi pe-0"
                        :class="{ 'bi-caret-down-fill': view == 'date', 'bi-caret-up-fill': view != 'date' }"></i>
                </div>

                <!-- Month Nav -->
                <div class="d-flex gap-3 me-3">
                    <i class="bi bi-chevron-left rounded-circle dp-nav-chev" @click="goBack" v-ripple></i>
                    <i class="bi bi-chevron-right rounded-circle dp-nav-chev" @click="goForward" v-ripple></i>
                </div>
            </div>

            <!-- Grid -->
            <div class="grid date-grid" v-if="view === 'date'">
                <div class="day-of-week text-secondary" v-for="day in daysOfWeek">{{ day }}</div>
                <div v-for="date in daysInMonth" :key="date" class="result-item bg-hover-gray"
                    :class="{ 'bg-dark text-white': date.day == currentDay && date.month == currentMonth, 'empty': date.empty }"
                    @click="selectDate(date)">
                    {{ date.day }}
                </div>
            </div>
            <div class="grid month-grid" v-else-if="view === 'month'">
                <div v-for="month in months" :key="month" class="result-item bg-hover-gray"
                    :class="{ 'bg-dark text-white': month == months[currentMonth] }" @click="selectMonth(month)">
                    {{ month.slice(0, 3) }}
                </div>
            </div>
            <div class="grid year-grid" v-else-if="view === 'year'">
                <div v-for="year in yearRange" :key="year" :ref="setYearRef(year)" class="result-item bg-hover-gray"
                    :class="{ 'bg-dark text-white': year == currentYear }" @click="selectYear(year)">
                    {{ year }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, nextTick, computed, watch } from 'vue';

const props = defineProps({
	questionDescription: String,
	questionSubText: String,
	data: Object,
	answer: {
		type: Array,
		default: () => []
	}
});

const emit = defineEmits(['question-answered']);

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const daysOfWeek = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const currentDate = new Date();
const showDatePicker = ref(false);
const view = ref('date');
const selectedDate = ref((props.answer && props.answer.length > 0) ? new Date(props.answer[0].value).toDateString() : 'Please select a date');
const currentDay = ref(selectedDate.value !== 'Please select a date' ? new Date(selectedDate.value).getDate() : currentDate.getDate());
const currentMonth = ref(selectedDate.value !== 'Please select a date' ? new Date(selectedDate.value).getMonth() : currentDate.getMonth());
const currentYear = ref(selectedDate.value !== 'Please select a date' ? new Date(selectedDate.value).getFullYear() : currentDate.getFullYear());
const yearElements = reactive({});

watch(selectedDate, (newVal, oldVal) => {
    const dateObj = new Date(newVal);
	let answerToReturn = [
		{ value: dateObj.toISOString(), text: newVal }
	]
    emit('question-answered', answerToReturn);
})

const setYearRef = (year) => {
    return (el) => {
        if (el) {
            yearElements[year] = el;
        }
    };
};

watch(view, (newVal) => {
    if (newVal === 'year') {
        nextTick(() => {
            if (yearElements[currentYear.value]) {
                yearElements[currentYear.value].scrollIntoView({
                    block: 'center'
                });
            }
        });
    }
});

const daysInMonth = computed(() => {
    const numDays = new Date(currentYear.value, currentMonth.value + 1, 0).getDate();
    let firstDay = new Date(currentYear.value, currentMonth.value, 1).getDay();

    firstDay = (firstDay === 0) ? 6 : firstDay - 1;

    let dates = [];
    for (let i = 0; i < firstDay; i++) {
        dates.push({ day: '', empty: true });
    }
    for (let i = 1; i <= numDays; i++) {
        dates.push({ day: i, month: currentMonth.value, empty: false });
    }
    return dates;
});


const yearRange = computed(() => {
    const startYear = 1940;
    const endYear = 2050;
    return Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
});

const toggleDatePicker = () => {
    view.value = 'date';
    showDatePicker.value = !showDatePicker.value;
}

const selectDate = (dateObj) => {
    if (dateObj.empty) return;
    const date = dateObj.day;
    selectedDate.value = new Date(currentYear.value, currentMonth.value, date).toDateString();
    currentDay.value = new Date(currentYear.value, currentMonth.value, date).getDate();
    toggleDatePicker();
    view.value = 'date';
}

const selectMonth = (month) => {
    currentMonth.value = months.indexOf(month);
    selectedDate.value = new Date(currentYear.value, currentMonth.value, currentDay.value).toDateString();
    view.value = 'date';
}

const selectYear = (year) => {
    currentYear.value = year;
    selectedDate.value = new Date(currentYear.value, currentMonth.value, currentDay.value).toDateString();
    view.value = 'month';
}

const toggleView = () => {
    view.value = view.value == 'date' ? 'year' : 'date';
}

const goBack = () => {
    if (currentMonth.value > 0) {
        currentMonth.value = currentMonth.value - 1
    } else {
        currentMonth.value = 11
        currentYear.value = currentYear.value - 1
    }
    selectedDate.value = new Date(currentYear.value, currentMonth.value, currentDay.value).toDateString();
}

const goForward = () => {
    if (currentMonth.value < 11) {
        currentMonth.value = currentMonth.value + 1
    } else {
        currentMonth.value = 0
        currentYear.value = currentYear.value + 1
    }
    selectedDate.value = new Date(currentYear.value, currentMonth.value, currentDay.value).toDateString();
}
</script>

<style scoped lang="scss">
.input-container {
    height: 47.5px;
}

.datepicker-container {
    position: relative;

    .bg-hover-gray {
        &:not(.empty):hover {
            background-color: #f5f5f7;
        }
    }

    .picker-popup {
        border: 0 !important;
        border-radius: .9em;
        margin-top: 0.75em;
        box-shadow: 0 0.1rem 0.7rem 0 rgba(0, 0, 0, 0.175);
        position: relative;
        z-index: 10;
        height: 25em;

        .picker-header {
            box-shadow: rgba(0, 0, 0, 0.1) 0px 2px 6px -2px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;

            .dp-nav-btn {
                width: auto;
                margin-left: 1em;
                padding-right: 1.05rem;
                height: 40px;
                padding-left: 0.3em;

                i {
                    font-size: 12px;
                    height: auto;
                    width: auto;
                }
            }

            .dp-nav-chev {
                font-weight: bold;
                padding: 0.65em;
                text-align: center;
                border-radius: 50%;
                width: 2.5em;
                height: 2.5em;
                line-height: 1.25em;
                font-size: 16px;
                display: initial;

                &:hover {
                    background-color: #f5f5f7;
                }
            }

        }

        .result-item {
            &:not(.empty) {
                cursor: pointer;
            }
        }

        .grid {
            overflow: hidden;
            padding-top: 0.5em;
            display: grid;
            justify-items: center;
            grid-gap: 0.5em;
        }

        .date-grid {
            grid-template-columns: auto auto auto auto auto auto auto;

            .result-item {
                padding: 0.65em;
                text-align: center;
                border-radius: 50%;
                width: 2.5em;
                height: 2.5em;
                line-height: 1.25em;
            }
        }


        .year-grid,
        .month-grid {
            grid-gap: 0.75em;

            .result-item {
                text-align: center;
                border-radius: 2em;
                height: 40px;
                width: 5em;
                line-height: 40px;
            }
        }

        .year-grid {
            overflow: auto;
            grid-template-columns: auto auto auto auto;
        }

        .month-grid {
            grid-template-columns: auto auto auto auto;

        }

        .caret {
            content: '';
            position: absolute;
            display: block;
            width: 12px;
            height: 12px;
            box-shadow: -3px -3px 0.4rem 0 rgba(0, 0, 0, 0.13);
            background-color: #fff;
            z-index: -1;
            top: 0;
            left: 18px;
            -webkit-transform: translateY(-50%) rotate(45deg);
            transform: translateY(-50%) rotate(45deg);
        }
    }
}

.dd-icon {
    font-size: 24px;
}

.hasResults .results {
    display: block;
}
</style>